import { ServiceContractTypes } from '@uptime-bff/api-contract';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import {
  MaintenanceAssetListSortingProps,
  MaintenanceAssetListSortOrder,
} from 'feature/maintenance/models/maintenanceReponse.model';
import type {
  AssetTypeFilter,
  MaintenanceAssetListItem,
  MaintenanceAssetListSorting,
  QuickFilter,
} from 'feature/maintenance/models/maintenanceReponse.model';

export const DEFAULT_MAINTENANCE_SORTING: MaintenanceAssetListSorting = {
  field: MaintenanceAssetListSortingProps.MaintenanceDue,
  order: MaintenanceAssetListSortOrder.Ascending,
};

interface MaintenanceState {
  selectedMaintenanceId?: string;
  activeQuickFilter?: QuickFilter;
  activeAssetTypeFilter: AssetTypeFilter;
  activeFocusCustomer: string[];
  activeServiceContractFilter: ServiceContractTypes[];
  searchTerm: string;
  maintenanceSorting?: MaintenanceAssetListSorting;
  maintenanceList: { [key: string]: MaintenanceAssetListItem };
}

export const defaultState: MaintenanceState = {
  selectedMaintenanceId: undefined,
  activeQuickFilter: undefined,
  activeAssetTypeFilter: 'all',
  activeFocusCustomer: [],
  activeServiceContractFilter: [],
  searchTerm: '',
  maintenanceSorting: DEFAULT_MAINTENANCE_SORTING,
  maintenanceList: {},
};

const maintenanceListToMap = (
  list: MaintenanceAssetListItem[],
): { [key: string]: MaintenanceAssetListItem } => {
  const payloadMap: { [key: string]: MaintenanceAssetListItem } = {};
  for (const asset of list) {
    payloadMap[asset.id] = asset;
  }
  return payloadMap;
};

const slice = createSlice({
  name: 'maintenance',
  initialState: defaultState,
  reducers: {
    toggleQuickFilter: (state, { payload }: PayloadAction<QuickFilter>) => {
      state.activeQuickFilter = state.activeQuickFilter === payload ? undefined : payload;
    },
    setAssetTypeFilter: (state, { payload }: PayloadAction<AssetTypeFilter>) => {
      state.activeAssetTypeFilter = payload;
    },
    setSearchTerm: (state, { payload }: PayloadAction<string>) => {
      state.searchTerm = payload;
    },
    setFocusCustomer: (state, { payload }: PayloadAction<string[]>) => {
      state.activeFocusCustomer = payload;
    },
    setServiceContractFilter: (state, { payload }: PayloadAction<ServiceContractTypes[]>) => {
      state.activeServiceContractFilter = payload;
    },
    setMaintenanceSorting: (state, { payload }: PayloadAction<MaintenanceAssetListSorting>) => {
      state.maintenanceSorting = payload;
    },
    setMaintenanceList: (state, { payload }: PayloadAction<MaintenanceAssetListItem[]>) => {
      state.maintenanceList = maintenanceListToMap(payload);
    },
    appendMaintenanceToList: (state, { payload }: PayloadAction<MaintenanceAssetListItem[]>) => {
      state.maintenanceList = { ...state.maintenanceList, ...maintenanceListToMap(payload) };
    },
    clearAllFilters: (state) => {
      state.activeQuickFilter = undefined;
      state.activeAssetTypeFilter = 'all';
      state.activeFocusCustomer = [];
      state.activeServiceContractFilter = [];
      state.searchTerm = '';
      state.maintenanceSorting = DEFAULT_MAINTENANCE_SORTING;
    },
  },
});

export const {
  toggleQuickFilter,
  setAssetTypeFilter,
  setSearchTerm,
  setFocusCustomer,
  setServiceContractFilter,
  setMaintenanceSorting,
  setMaintenanceList,
  appendMaintenanceToList,
  clearAllFilters,
} = slice.actions;
export default slice.reducer;
